/**
 * Draggable Header Component
 *
 * Table header cell with drag-and-drop support for column reordering.
 */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender } from "@tanstack/react-table";
import { GripVertical } from "lucide-react";
import React, { useCallback } from "react";
import { cn } from "@/shared/lib/utils";
import {
  getResizeHandleProps,
  getHeaderStyle,
  getHeaderClassName,
  getHeaderText,
} from "../utils";
import { SortIndicator } from "./SortIndicator";
import type { AppHeader } from "../features";
import type { TanStackColumn } from "../types";
import type { RowData } from "@tanstack/react-table";

type DraggableHeaderProps<T extends RowData> = {
  header: AppHeader<T>
  enableSorting: boolean
  onChangeSort?: (field: string) => void
  enableColumnResizing: boolean
  enableColumnPinning: boolean
  enableColumnOrdering: boolean
  enableColumnFiltering: boolean
  isLastColumn?: boolean
};

const renderHeaderContent = <T extends RowData>(
  header: AppHeader<T>,
  canReorder: boolean,
  enableSorting: boolean,
  attributes: ReturnType<typeof useSortable>["attributes"],
  listeners: ReturnType<typeof useSortable>["listeners"],
  column: TanStackColumn<T>,
) => {
  const hasCustomHeader = typeof header.column.columnDef.header === "function";

  return (
    <div className="flex items-center gap-2 w-full h-full">
      {canReorder && (
        <div
          {...attributes}
          {...listeners}
          data-no-sort
          className="-ml-1 cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3 h-3 text-gray-400" />
        </div>
      )}
      <div
        className={cn(
          "flex-1 min-w-0 !text-left flex items-center h-full",
          column.disableTruncate ? "whitespace-nowrap" : "line-clamp-2 text-wrap break-words leading-tight",
          !hasCustomHeader && enableSorting && header.column.getIsSorted() && "text-blue-600",
        )}
        title={column.disableTruncate ? undefined : getHeaderText(column)}
      >
        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {enableSorting && !hasCustomHeader && (
        <div className="flex-shrink-0 ml-auto">
          <SortIndicator header={header} />
        </div>
      )}
    </div>
  );
};

export const DraggableHeader = <T extends RowData>({
  header,
  enableSorting,
  onChangeSort,
  enableColumnResizing,
  enableColumnPinning,
  enableColumnOrdering,
  enableColumnFiltering: _enableColumnFiltering,
  isLastColumn,
}: DraggableHeaderProps<T>) => {
  const column = header.column.columnDef as TanStackColumn<T>;
  const isSpecialColumn = column.isSelectionColumn || header.column.id === "actions" || header.column.id === "details";
  const canReorder = enableColumnOrdering && !isSpecialColumn && column.enableOrdering !== false;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.column.id,
    disabled: !canReorder,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSort = useCallback(
    (e: React.MouseEvent) => {
      if (!enableSorting || !header.column.getCanSort()) {
        return;
      }
      const target = e.target as HTMLElement;
      if (target.closest("[data-no-sort]")) {
        return;
      }
      if (onChangeSort) {
        onChangeSort(header.column.id);
        return;
      }

      const currentTarget = e.currentTarget as HTMLElement;
      const sortButton = currentTarget.querySelector("button");
      if (sortButton && !target.closest("button")) {
        sortButton.click();
        return;
      }

      header.column.getToggleSortingHandler()?.(e);
    },
    [enableSorting, header.column, onChangeSort],
  );

  return (
    <th
      ref={setNodeRef}
      data-column-id={header.column.id}
      style={getHeaderStyle(header, enableColumnPinning, style)}
      className={getHeaderClassName(column, enableSorting, header, isDragging)}
      onClick={handleSort}
    >
      {renderHeaderContent(header, canReorder, enableSorting, attributes, listeners, column)}
      {enableColumnResizing && header.column.getCanResize() && (
        <div {...getResizeHandleProps(header, isLastColumn)} />
      )}
    </th>
  );
};
